/* eslint-disable no-bitwise */
import * as Permissions from './permission_enum';
import {
  ADD_GROUP_REQUEST,
  ADD_GROUP_SUCCESS,
  FETCH_TEMPLATE_FOLDER_SUCCESS,
  FETCH_GROUP_REQUEST,
  FETCH_GROUP_SUCCESS,
  FETCH_GROUP_DETAILS_REQUEST,
  FETCH_GROUP_DETAILS_SUCCESS,
  UPDATE_GROUP_NAME,
  UPDATE_ALLFORM_PERMISSIONS,
  UPDATE_BINARY_PERMISSION,
  UPDATE_FOLDER_PERMISSION,
  ADD_GROUP_FAILURE,
  FETCH_TEMPLATE_FOLDER_FAILURE,
  CREATE_GROUP_REQUEST,
  CREATE_GROUP_FAILURE,
  FETCH_GROUP_FAILURE,
  FETCH_GROUP_DETAILS_FAILURE,
  SAVE_GROUP_REQUEST,
  SAVE_GROUP_SUCCESS,
  SAVE_GROUP_FAILURE,
  DELETE_GROUP_REQUEST,
  DELETE_GROUP_SUCCESS,
  DELETE_GROUP_FAILURE,
  CLEAR_ERROR,
  UPDATE_ALL_BINARY_PERMISSION
} from './actions';
import { GroupDetails, GroupsState } from '../../usersgroups/usergroup-types';
import { produce } from 'immer';
import { PERMISSION_CONFIGS } from './folderPermissionHelper';

function initializeListState(): GroupsState {
  return {
    isRoot: false,
    isFetching: false,
    id: '',
    name: '',
    isAdminGroup: false,
    groups: [],
    breadcrumbs: []
  } as GroupsState;
}

function initializeGroupState(previousState: GroupsState): GroupsState {
  return {
    breadcrumbs: previousState?.groupList?.breadcrumbs || [],
    error: '',
    childGroupCount: 0,
    name: '',
    id: '',
    parentName: '',
    parentId: '',
    isRoot: false,
    isAdminGroup: false,
    binaryPermissions: 0,
    inheritedPermissions: 0,
    folders: [],
    isFetching: false
  } as GroupsState;
}

function getGroupList(groupListState: GroupsState = initializeListState(), data?, groupId?: string): GroupsState {
  const sortedGroups = [];
  let adminGroup = null;

  if (data.groups) {
    for (const group of data.groups) {
      if (!group.isAdminGroup) {
        sortedGroups.push(group);
      } else {
        adminGroup = group;
      }
    }

    if (adminGroup) {
      sortedGroups.splice(0, 0, adminGroup);
    }
    // eslint-disable-next-line no-param-reassign
    data.groups = sortedGroups;
  }

  return {
    ...groupListState,
    data,
    isRoot: !groupId,
    id: !groupId ? '' : groupId,
    isAdminGroup: !!data.isAdminGroup
  };
}

function setFolders(state: GroupsState, details): GroupsState {
  for (const folder of details.folders) {
    folder.inheritedPermissions = 0;
    folder.permissions = 0;
  }
  state.error = '';
  state.isFetching = false;
  state.folders = details.folders;
  return state;
}

function updateFolderPermissions(state: GroupsState, payload): GroupsState {
  const f = state.folders.find((f) => {
    return f.id === payload.id;
  });

  const previousPermission = f.permissions;
  let permission = previousPermission ^ payload.permission;

  permission = getPermissionValue(previousPermission, payload.permission);

  f.permissions = permission;
  f.error = '';
  return state;
}

function getPermissionValue(currentPermission: number, payloadPermission: number) {
  const { [payloadPermission]: config } = PERMISSION_CONFIGS;

  return config.togglePermission(currentPermission);
}

function updateBinaryPermission(state: GroupsState, payload): GroupsState {
  const permission = state.binaryPermissions ^ payload.permission;
  state.binaryPermissions = permission;
  state.error = '';
  return state;
}

function setGroupDetails(state: GroupsState, details: GroupDetails, isNew = false): GroupsState {
  if (isNew) {
    for (const folder of details.folderPermissions) {
      // eslint-disable-next-line operator-assignment
      folder.inheritedPermissions = folder.permissions ^ folder.inheritedPermissions;
      folder.permissions = 0;
    }
  }

  state.data = details.data;

  state.id = isNew ? '' : details.id;
  state.name = isNew ? '' : details.name;
  state.parentId = !isNew ? '' : details.id;
  state.parentName = !isNew ? '' : details.name;
  state.isAdminGroup = details.isAdminGroup;
  state.binaryPermissions = isNew ? 0 : details.binaryPermissions;
  state.inheritedPermissions = isNew ? details.binaryPermissions ^ details.inheritedPermissions : details.inheritedPermissions;
  state.error = '';
  state.isFetching = false;
  state.childGroupCount = details.childGroupCount;
  state.folders = details.folderPermissions;

  return state;
}

export default function (state: GroupsState = initializeListState(), action): GroupsState {
  if (!action) {
    return state;
  }
  return produce(state, (draft) => reduce(draft, action));
}

function reduce(state: GroupsState = initializeListState(), action): GroupsState {
  switch (action.type) {
    case FETCH_GROUP_DETAILS_REQUEST:
    case ADD_GROUP_REQUEST:
      return { ...initializeGroupState(state), ...action.payload };
    case ADD_GROUP_SUCCESS:
      return setGroupDetails(state, action.payload, true);
    case FETCH_TEMPLATE_FOLDER_SUCCESS:
      return setFolders(state, action.payload);
    case FETCH_GROUP_REQUEST:
      return getGroupList(state, action.payload);
    case FETCH_GROUP_SUCCESS:
      getGroupList(state, action.payload.data, action.payload.id);
      state.isFetching = false;
      return state;
    case FETCH_GROUP_DETAILS_SUCCESS:
      return setGroupDetails(state, action.payload);
    case UPDATE_GROUP_NAME:
      state.name = action.payload.name;
      state.error = '';
      return state;
    case UPDATE_ALLFORM_PERMISSIONS:
      state.error = '';
      state.binaryPermissions =
        action.payload.allFormPermissions === true
          ? state.binaryPermissions | Permissions.ALL_FORM_PERMISSIONS
          : state.binaryPermissions ^ Permissions.ALL_FORM_PERMISSIONS;
      return state;
    case UPDATE_ALL_BINARY_PERMISSION:
      state.error = '';
      state.binaryPermissions =
        action.payload.allBinaryPermission === true
          ? state.binaryPermissions | Permissions.ALL_MANAGE_PERMISSIONS
          : state.binaryPermissions ^ Permissions.ALL_MANAGE_PERMISSIONS;
      return state;

    case UPDATE_BINARY_PERMISSION:
      return updateBinaryPermission(state, action.payload);
    case UPDATE_FOLDER_PERMISSION:
      return updateFolderPermissions(state, action.payload);

    case ADD_GROUP_FAILURE:
    case FETCH_TEMPLATE_FOLDER_FAILURE:
    case CREATE_GROUP_REQUEST:
    case CREATE_GROUP_FAILURE:
    case FETCH_GROUP_FAILURE:
    case FETCH_GROUP_DETAILS_FAILURE:
    case SAVE_GROUP_REQUEST:
    case SAVE_GROUP_SUCCESS:
    case SAVE_GROUP_FAILURE:
    case DELETE_GROUP_REQUEST:
    case DELETE_GROUP_SUCCESS:
    case DELETE_GROUP_FAILURE:
    case CLEAR_ERROR:
      return { ...state, ...action.payload };

    default:
      return state;
  }
}
